import { useCallback, useEffect, useMemo, useState } from 'react';

import { TFunction, Trans, useTranslation } from '@lyearn/i18n';
import { CONTENT_TYPE_LOWER_CASE_i18n_CONFIG } from '@/modules/content/config';
import {
  PublishContentConfig,
  RecalibrateConfigInput,
  ResourceTypeEnum,
  ResourceVersionTypeEnum,
} from '@/types/schema';

import { onPublishType } from '../../types';
import useDefaultPublishConfig from './useDefaultPublishConfig';

type RecalibrateConfigInputType = keyof RecalibrateConfigInput;

const getPublishOptions = (
  t: TFunction,
  isLearnerProgessAffected: boolean,
  allowMinorVersionPublish?: boolean,
  contentType?: ResourceTypeEnum,
) => [
  {
    id: ResourceVersionTypeEnum.Minor,
    text: t('Common:Form.UpdateExistingVersion'),
    description: (
      <Trans
        i18nKey="Common:Form.UpdateExistingVersionDescription"
        values={{
          contentType: contentType
            ? t(CONTENT_TYPE_LOWER_CASE_i18n_CONFIG[contentType])
            : 'content',
        }}
      />
    ),
    disabled: !allowMinorVersionPublish,
  },
  {
    id: ResourceVersionTypeEnum.Major,
    text: t('Common:Form.CreateNewVersion'),
    description: isLearnerProgessAffected
      ? t('Common:Form.CreateNewVersionDescription')
      : t('Common:Form.CreateNewVersionWithoutUpdate'),
    options: isLearnerProgessAffected
      ? [
          {
            id: 'recalibrateCompletedPerformances' as RecalibrateConfigInputType,
            text: (
              <Trans
                i18nKey="Common:Form.AffectLearnersWhoCompletedCourse"
                values={{
                  contentType: contentType
                    ? t(CONTENT_TYPE_LOWER_CASE_i18n_CONFIG[contentType])
                    : 'content',
                }}
              />
            ),
          },
        ]
      : undefined,
  },
];

const usePublishChanges = (
  onPublish: onPublishType,
  onClose: () => void,
  isLearnerProgessAffected: boolean,
  allowMinorVersionPublish?: boolean,
  contentType?: ResourceTypeEnum,
) => {
  const { t } = useTranslation();
  const defaultPublishConfig = useDefaultPublishConfig(allowMinorVersionPublish);
  const [publishConfig, setPublishConfig] = useState<PublishContentConfig>(defaultPublishConfig);

  useEffect(() => {
    setPublishConfig(defaultPublishConfig);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowMinorVersionPublish]);

  const [hasError, setError] = useState(false);
  const publishOptions = useMemo(
    () => getPublishOptions(t, isLearnerProgessAffected, allowMinorVersionPublish, contentType),
    [t, isLearnerProgessAffected, contentType, allowMinorVersionPublish],
  );
  const { note = '' } = publishConfig;

  const validateNote = useCallback((noteText?: string | null) => {
    const error = !noteText?.trim();
    setError(error);
    return !error;
  }, []);

  const onNoteTextChange = useCallback(
    (noteValue: any) => {
      validateNote(noteValue);
      setPublishConfig({ ...publishConfig, note: noteValue });
    },
    [publishConfig, validateNote],
  );

  const onVersionChange = useCallback(
    (version: ResourceVersionTypeEnum) => {
      setPublishConfig({ ...publishConfig, versionType: version });
    },
    [publishConfig],
  );

  const onRecalibrateChange = useCallback(
    (recalibrateConfig: string, value: boolean) => {
      const updatedRecalibrateConfig = {
        ...publishConfig.recalibrateConfig,
        [recalibrateConfig]: value,
      };
      setPublishConfig({ ...publishConfig, recalibrateConfig: updatedRecalibrateConfig });
    },
    [publishConfig],
  );

  const onPublishChanges = useCallback(() => {
    if (validateNote(note)) {
      onPublish(publishConfig);
      onClose();
    }
  }, [note, onClose, onPublish, publishConfig, validateNote]);

  return {
    onNoteTextChange,
    publishConfig,
    hasError,
    publishOptions,
    onVersionChange,
    onRecalibrateChange,
    onPublishChanges,
  };
};

export default usePublishChanges;
